import compat from './compat.json';

/** One raise of the number a web build sends. */
export interface ApiCompatRaise {
  /** What web builds from this raise on stop relying on. */
  reason: string;
  /**
   * Routes those builds do not call, named as `compatRoutes.json` names them.
   * The API may remove one once its minimum reaches this raise.
   */
  retires?: string[];
}

interface ApiCompat {
  minimum: number;
  raises: ApiCompatRaise[];
}

const { minimum, raises }: ApiCompat = compat;

/**
 * Every raise of the number a web build sends, oldest first. A raise is only
 * ever appended, so two branches that raise at once conflict rather than merge
 * into one. See `.agents/rules/api-compat.md`.
 */
export const API_COMPAT_RAISES: readonly ApiCompatRaise[] = raises;

/** The number this web build sends: one more than the raises it carries. */
export const API_COMPAT_VERSION = raises.length + 1;

/**
 * The oldest number the API serves; it answers a lower one with a 426. It only
 * rises to a number the web live on `dev` and `prod` already sends, so the
 * reload it asks for lands on a build it accepts.
 */
export const MIN_API_COMPAT_VERSION = minimum;

/** The number a raise moves web builds onto. */
export const getRaiseVersion = (index: number) => index + 2;

/** The first number whose builds do not call a route, or undefined while every build calls it. */
export const findRetiringVersion = (
  route: string,
  from: readonly ApiCompatRaise[] = API_COMPAT_RAISES
): number | undefined => {
  const index = from.findIndex(raise => raise.retires?.includes(route));
  return index === -1 ? undefined : getRaiseVersion(index);
};

export const API_COMPAT_VERSION_HEADER = 'X-Api-Compat-Version';
